(() => {
  const SELECTORS = {
    hero: '[data-families-hero]',
    reasons: '[data-families-reasons]',
    reason: '[data-families-reason]',
    progress: '[data-families-progress]',
    label: '[data-families-progress-label]',
    segment: '[data-families-progress-segment]',
    builder: '[data-families-builder], #families-builder',
    builderBox: '.build-a-bundle__info-container'
  };

  let cleanupCurrentPage = null;

  const initialiseBuilderPagination = (builder) => {
    if (!builder || builder.dataset.familiesPagination !== 'true') return () => {};

    const cardSelector = [
      '.build-a-bundle__product-card',
      '.how-it-works-bab-card',
      '.whats-with-blocks-bab-card',
      '.taste-guarantee-bab-card'
    ].join(',');
    const cards = [...builder.querySelectorAll(cardSelector)];
    const viewMoreButton = builder.querySelector('[data-families-view-more]');

    // The grid is 2 columns on mobile and 3 from the theme's `desktop:`
    // breakpoint up, so the batch size has to change with the breakpoint or
    // "View more" leaves a part-filled last row on desktop. The cards the LP
    // hides in the desktop grid drop out of the count too: counting a
    // display:none card would render a row of 3 as a row of 2.
    const desktopQuery = window.matchMedia(
      `(min-width: ${Math.max(1, Number(builder.dataset.familiesDesktopBreakpoint) || 1024)}px)`
    );
    let desktopHiddenSelector = (builder.dataset.familiesDesktopHiddenCards || '').trim();
    if (desktopHiddenSelector) {
      try {
        document.createDocumentFragment().querySelector(desktopHiddenSelector);
      } catch (error) {
        desktopHiddenSelector = '';
      }
    }

    const readCount = (value, fallback) => Math.max(1, Number(value) || fallback);

    let pagedCards = cards;
    let initialCount = 10;
    let increment = 10;
    // Counted in clicks, not cards, so a breakpoint change keeps the customer
    // roughly where they were instead of collapsing the grid back to one batch.
    let expansions = 0;
    let renderQueued = false;

    if (!cards.length || !viewMoreButton) return () => {};

    const readBreakpoint = () => {
      const isDesktop = desktopQuery.matches;

      initialCount = isDesktop
        ? readCount(builder.dataset.familiesInitialCountDesktop, 9)
        : readCount(builder.dataset.familiesInitialCount, 10);
      increment = isDesktop
        ? readCount(builder.dataset.familiesCardIncrementDesktop, 9)
        : readCount(builder.dataset.familiesCardIncrement, 10);
      pagedCards = isDesktop && desktopHiddenSelector
        ? cards.filter((card) => !card.matches(desktopHiddenSelector))
        : cards;
    };

    const render = () => {
      renderQueued = false;
      const visibleLimit = initialCount + (expansions * increment);
      const filterVisibleCards = pagedCards.filter((card) => !card.classList.contains('hidden'));

      cards.forEach((card) => {
        const filteredIndex = filterVisibleCards.indexOf(card);
        const shouldHide = filteredIndex === -1 || filteredIndex >= visibleLimit;
        card.classList.toggle('families-card--page-hidden', shouldHide);
      });

      const remainingCount = Math.max(0, filterVisibleCards.length - visibleLimit);
      viewMoreButton.hidden = remainingCount === 0;
      viewMoreButton.textContent = remainingCount > increment
        ? `View ${increment} more`
        : remainingCount > 0
          ? `View ${remainingCount} more`
          : 'View more';
      viewMoreButton.setAttribute('aria-expanded', remainingCount === 0 ? 'true' : 'false');
    };

    const queueRender = () => {
      if (renderQueued) return;
      renderQueued = true;
      window.requestAnimationFrame(render);
    };

    const onViewMore = () => {
      expansions += 1;
      render();
    };

    const onBreakpointChange = () => {
      readBreakpoint();
      render();
    };

    // The Adults/Kids anchor buttons scroll to their collection, but cards
    // beyond the pagination cut are display:none, so the Kids section would
    // measure near-empty and the scroll would land wrong. Capture phase runs
    // on this ancestor BEFORE the theme's own listener on the button itself,
    // so the full grid is laid out by the time the theme computes offsets.
    const onNavCapture = (event) => {
      if (!event.target.closest('.build-a-bundle__sticky-nav-trigger')) return;
      const totalCards = pagedCards.filter((card) => !card.classList.contains('hidden')).length;
      if (initialCount + (expansions * increment) >= totalCards) return;
      expansions = Math.ceil((totalCards - initialCount) / increment);
      render();
    };

    const classObserver = new MutationObserver(queueRender);
    cards.forEach((card) => classObserver.observe(card, { attributes: true, attributeFilter: ['class'] }));
    viewMoreButton.addEventListener('click', onViewMore);
    builder.addEventListener('click', onNavCapture, true);
    if (desktopQuery.addEventListener) desktopQuery.addEventListener('change', onBreakpointChange);
    else if (desktopQuery.addListener) desktopQuery.addListener(onBreakpointChange);
    readBreakpoint();
    render();

    return () => {
      classObserver.disconnect();
      viewMoreButton.removeEventListener('click', onViewMore);
      builder.removeEventListener('click', onNavCapture, true);
      if (desktopQuery.removeEventListener) desktopQuery.removeEventListener('change', onBreakpointChange);
      else if (desktopQuery.removeListener) desktopQuery.removeListener(onBreakpointChange);
      cards.forEach((card) => card.classList.remove('families-card--page-hidden'));
    };
  };

  const initialiseGuaranteeQuotes = () => {
    const swipe = document.querySelector('[data-families-guarantee-swipe]');
    const track = swipe?.querySelector('[data-families-guarantee-track]');
    const quotes = swipe ? [...swipe.querySelectorAll('[data-families-guarantee-quote]')] : [];
    const dots = swipe ? [...swipe.querySelectorAll('[data-families-guarantee-dot]')] : [];
    let updateQueued = false;

    if (!track || quotes.length < 2 || quotes.length !== dots.length) return () => {};

    const setActiveDot = (activeIndex) => {
      dots.forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    const updateFromScroll = () => {
      updateQueued = false;
      const activeQuote = quotes.reduce((closest, quote, index) => {
        const distance = Math.abs(quote.offsetLeft - track.scrollLeft);
        return distance < closest.distance ? { index, distance } : closest;
      }, { index: 0, distance: Number.POSITIVE_INFINITY });
      setActiveDot(activeQuote.index);
    };

    const onScroll = () => {
      if (updateQueued) return;
      updateQueued = true;
      window.requestAnimationFrame(updateFromScroll);
    };

    const dotHandlers = dots.map((dot, index) => {
      const handler = () => track.scrollTo({
        left: quotes[index].offsetLeft,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
      dot.addEventListener('click', handler);
      return handler;
    });

    track.addEventListener('scroll', onScroll, { passive: true });
    setActiveDot(0);

    return () => {
      track.removeEventListener('scroll', onScroll);
      dots.forEach((dot, index) => dot.removeEventListener('click', dotHandlers[index]));
    };
  };

  const initialiseDelegatedPopups = () => {
    // Rows outside the builder's template root (e.g. the Dragons' Den card's
    // delivery row) cannot be bound by app.js, whose popup triggers are scoped
    // to .build-a-bundle-template. Delegate their clicks to the builder's own
    // trigger so the theme's popup machinery stays the single owner.
    const delegates = [...document.querySelectorAll('[data-families-delegated-popup]')];
    if (!delegates.length) return () => {};

    const handlers = delegates.map((el) => {
      const handler = (event) => {
        const target = el.dataset.familiesDelegatedPopup;
        const trigger = document.querySelector(`.build-a-bundle-template .${target}-popup-trigger`);
        if (!trigger) return; // no popup on the page: an <a> delegate falls back to its href
        event.preventDefault();
        trigger.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      };
      el.addEventListener('click', handler);
      return handler;
    });

    return () => {
      delegates.forEach((el, index) => el.removeEventListener('click', handlers[index]));
    };
  };

  // The equation line ("2 for you · 2 for the kids") belongs to one bundle, but the
  // theme's tab script only clears `.active` from elements it has a counterpart for.
  // Family was the only bundle with an equation, so its line stayed on screen over the
  // Dragons and Veggie tabs, claiming a split those bundles do not have (Grace, 19 Aug).
  // The h3 titles DO swap correctly, so mirror them: whichever title is active decides
  // which equation is active. Watching the titles rather than the tab buttons means this
  // holds however the theme changes tabs.
  const initialiseBundleEquations = () => {
    const sections = [...document.querySelectorAll('.section-prebuilt-bundles')];
    if (!sections.length) return () => {};

    const observers = sections.map((section) => {
      const equations = [...section.querySelectorAll('.families-prebuilt__equation')];
      const titles = [...section.querySelectorAll('h3.bundle-title[data-product-id]')];
      if (!equations.length || !titles.length) return null;

      const sync = () => {
        const active = titles.find((t) => t.classList.contains('active'));
        if (!active) return;
        equations.forEach((eq) => {
          eq.classList.toggle('active', eq.dataset.productId === active.dataset.productId);
        });
      };

      const observer = new MutationObserver(sync);
      titles.forEach((t) => observer.observe(t, { attributes: true, attributeFilter: ['class'] }));
      sync();
      return observer;
    }).filter(Boolean);

    return () => observers.forEach((o) => o.disconnect());
  };

  const initialiseCustomiseBundle = () => {
    // "Customise your bundle" on a prebuilt card seeds the on-page builder with
    // that card's products by replaying clicks on the builder's own +/- controls.
    // Driving the theme's real updateBundle path keeps localStorage, the item
    // counter, milestone rewards, pricing and the progress toasts all correct,
    // so by the time the scroll lands the tracker already shows the seeded
    // bundle. Replace semantics: all decrements run before any increments so
    // the max-items cap can never block a seed.
    //
    // On THIS page there is no builder, so a second path is needed. The builder
    // rebuilds itself from localStorage.bundle on load, and the row markup in
    // snippets/lp-families-bundle-row.liquid already emits exactly the dataset
    // attributes the theme's own setupBundleObject reads (data-id is the variant
    // id, which is the key it stores under). So the cross-page seed is a
    // correctly shaped write before the anchor navigates. Verified live: seeding
    // the four family meals then loading /products/build-a-bundle brings the
    // builder up showing BUNDLE BOX 4 at the right price.
    const triggers = [...document.querySelectorAll('[data-families-customise-bundle]')];
    if (!triggers.length) return () => {};

    // REPLACE, not merge. See docs/adr/0005 for why. Short version: the CTA is
    // scoped to the bundle shown in its own section, so "Customise your bundle"
    // means the one on screen, and an LP is the shopper's starting point in
    // essentially every session. Replacing is idempotent, which kills the whole
    // class of double-tap and back-button bugs a merge creates, and it needs no
    // undo affordance, which we could not build anyway: the only place to put one
    // is /products/build-a-bundle, which is Stocked's own theme and off limits to
    // LP code (conduct law 6).
    //
    // This deliberately reads NOTHING from localStorage. No merge means no cap to
    // enforce, so the duplicated MAX_BUNDLE_ITEMS = 8 constant is gone, and with
    // it the risk of it drifting from the builder's real cap.

    const seedAcrossPages = (contents) => {
      const rows = [...contents.querySelectorAll('.bundle-contents-product[data-id]')];
      if (!rows.length) return;

      // Mirror the theme's setupBundleObject field for field, including its
      // parseFloat treatment, so the builder cannot tell our write from its own.
      // Blank compare-price attributes are dropped rather than written as NaN,
      // which JSON.stringify would turn into null.
      const wanted = {};
      rows.forEach((row) => {
        const data = row.dataset;
        if (!data.id) return;
        // Skip a pack the shop cannot currently sell. The section already blocks
        // Add to Order for the whole bundle when this happens, but this CTA sits
        // outside that guard, so without this the shopper is seeded an unbuyable
        // pack and only discovers it at the end. Landing with three and being
        // asked for a fourth is a far better recovery. Marker comes from
        // snippets/lp-families-bundle-row.liquid.
        if (data.familiesRowUnavailable === 'true') return;
        const item = {
          title: data.title,
          price: parseFloat(data.price),
          priceString: data.priceString,
          image: data.image,
          quantity: Math.max(1, Number(data.quantity) || 1),
        };
        if (data.comparePrice) item.comparePrice = parseFloat(data.comparePrice);
        if (data.comparePriceString) item.comparePriceString = data.comparePriceString;
        wanted[data.id] = item;
      });
      if (!Object.keys(wanted).length) return;

      // A fresh bundle every time: the id is new and the items are exactly what
      // the section is showing. Nothing from the previous bundle survives, which
      // is the point. Writing a clean object also clears any editing_bundle flag
      // left over from a half-finished subscription edit, so the builder treats
      // this as a new bundle rather than resuming an edit.
      const bundle = {
        bundleId: `bundle_${Date.now()}`,
        items: wanted,
      };

      try {
        window.localStorage.setItem('bundle', JSON.stringify(bundle));
      } catch (error) {
        // Private mode or a full quota: the anchor still navigates, the shopper
        // just lands on an empty builder rather than a broken one.
      }
    };

    const handlers = triggers.map((trigger) => {
      const handler = (event) => {
        const builder = document.querySelector('.build-a-bundle-template');
        // A trigger inside a [data-families-seed-scope] (the reviews carousel's
        // "Shop now" cards, 2026-09-02) seeds from that scope's hidden rows;
        // otherwise it is a pre-built bundle card and the active contents apply.
        const scope = trigger.closest('[data-families-seed-scope]');
        const section = trigger.closest('.section-prebuilt-bundles');
        const contents = scope || section?.querySelector('.bundle-contents.active') || section?.querySelector('.bundle-contents');
        if (!contents) return; // nothing to seed from: the anchor href takes over
        if (!builder) {
          seedAcrossPages(contents);
          return; // no preventDefault: the anchor navigates to the builder page
        }

        const wanted = new Set(
          [...contents.querySelectorAll('.bundle-contents-product[data-id]')]
            .map((row) => row.dataset.id)
            .filter(Boolean)
        );
        if (!wanted.size) return;

        const cards = [...builder.querySelectorAll('.build-a-bundle__product-card[data-id]')];
        if (!cards.some((card) => wanted.has(card.dataset.id))) return; // none purchasable here: fall back

        event.preventDefault();

        const currentQuantity = (id) => Number(window.bundle?.items?.[id]?.quantity) || 0;
        const clickTimes = (button, times) => {
          for (let i = 0; i < times; i += 1) {
            if (!button || button.disabled) return;
            button.click();
          }
        };

        cards.forEach((card) => {
          const target = wanted.has(card.dataset.id) ? 1 : 0;
          const surplus = currentQuantity(card.dataset.id) - target;
          if (surplus > 0) clickTimes(card.querySelector('.product-quantity__decrease'), surplus);
        });
        cards.forEach((card) => {
          const target = wanted.has(card.dataset.id) ? 1 : 0;
          const deficit = target - currentQuantity(card.dataset.id);
          if (deficit > 0) clickTimes(card.querySelector('.product-quantity__increase'), deficit);
        });

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        builder.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      };
      trigger.addEventListener('click', handler);
      return handler;
    });

    return () => {
      triggers.forEach((el, index) => el.removeEventListener('click', handlers[index]));
    };
  };

  const initialiseBundleItemPopups = () => {
    // Tapping an item inside the bundle box opens the same meal popup as the
    // builder's product cards: the tap is relayed to that card's own
    // popup trigger, so the theme's popup machinery stays the single owner.
    // Delegated from the box container because the theme re-renders the item
    // rows on every bundle mutation.
    const builder = document.querySelector('.build-a-bundle-template');
    const box = builder?.querySelector('.build-a-bundle__info-container');
    if (!builder || !box) return () => {};

    const openPopupForVariant = (variantId) => {
      if (!variantId) return;
      const card = builder.querySelector(`.build-a-bundle__product-card[data-id="${variantId}"]`);
      card?.querySelector('.build-a-bundle__product-card__popup-trigger')
        ?.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
    };

    const handler = (event) => {
      if (event.target.closest('button, a')) return; // leave the row's real controls alone

      const mobileItem = event.target.closest('.build-a-bundle__info-body-mobile-product-list-product');
      if (mobileItem) {
        openPopupForVariant(mobileItem.dataset.productId);
        return;
      }

      // The desktop thumbnails carry no id, only the item's image, so match
      // the src back to the bundle entry that rendered it.
      const thumb = event.target.closest('.desktop-bundle-image');
      if (thumb) {
        const src = thumb.querySelector('img')?.getAttribute('src');
        if (!src) return;
        const entry = Object.entries(window.bundle?.items || {}).find(([, item]) => item?.image === src);
        openPopupForVariant(entry?.[0]);
      }
    };

    box.addEventListener('click', handler);
    return () => box.removeEventListener('click', handler);
  };

  const initialiseInfoPopups = () => {
    // The dedicated production Build Your Bundle disclosure popups (taste
    // guarantee + delivery), rendered up front by the prebuilt section with the
    // same class contract as the production snippets. app.js scopes its click
    // bindings to .build-a-bundle-template, which this LP does not render, so
    // this copies only its state transition: add .active to open, remove it to
    // close. Ported verbatim from lp-lagomchef.js (the working precedent).
    const popups = [...document.querySelectorAll('[data-families-info-popup]')];
    if (!popups.length) return () => {};

    let activePopup = null;
    let activeTrigger = null;

    const lockBackground = () => {
      if (document.body.style.position === 'fixed') return;
      document.body.classList.add('overflow-y-hidden');
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    };

    const unlockBackground = () => {
      if (document.body.style.position !== 'fixed') return;
      document.body.classList.remove('overflow-y-hidden');
      const scrollY = Math.abs(parseInt(document.body.style.top || 0, 10));
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
      });
    };

    const close = ({ restoreFocus = true } = {}) => {
      if (!activePopup) return;
      activePopup.classList.remove('active');
      activePopup = null;
      unlockBackground();
      if (restoreFocus && activeTrigger?.isConnected) activeTrigger.focus({ preventScroll: true });
      activeTrigger = null;
    };

    const open = (key, trigger) => {
      const popup = popups.find((candidate) => candidate.dataset.familiesInfoPopup === key);
      if (!popup) return false;
      if (activePopup && activePopup !== popup) activePopup.classList.remove('active');
      activePopup = popup;
      activeTrigger = trigger;
      lockBackground();
      popup.classList.add('active');
      popup.querySelector('button[data-families-info-popup-close]')?.focus({ preventScroll: true });
      return true;
    };

    const onClick = (event) => {
      const trigger = event.target.closest('[data-families-delegated-popup]');
      if (trigger) {
        if (open(trigger.dataset.familiesDelegatedPopup, trigger)) event.preventDefault();
        return;
      }

      const closeControl = event.target.closest('[data-families-info-popup-close]');
      if (closeControl?.closest('[data-families-info-popup]') === activePopup) close();
    };

    const onKeydown = (event) => {
      if (event.key === 'Escape' && activePopup) close();
    };

    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);

    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeydown);
      close({ restoreFocus: false });
    };
  };

  const initialiseDishRowPopups = () => {
    // Tapping a dish row in a prebuilt bundle card opens that product's details,
    // the same content the build-a-bundle page shows on a product card.
    //
    // The builder is not on this LP, so there is no card to delegate a click to.
    // What IS here is the theme's popup shell: one .popup-container holding a
    // .popup-contents-output, opened by adding .active while the body takes
    // overflow-y-hidden (verified by firing an existing trigger and diffing the
    // DOM). The section renders the builder's own popup-contents snippet once per
    // unique product; this copies the right one into that shell.
    // Info rows count towards the guard as well as dish rows: this initialiser now
    // owns both, so a page with only the guarantee and delivery rows must still boot.
    const rows = [...document.querySelectorAll('[data-families-product-popup-trigger], [data-families-delegated-popup]')];
    const container = document.querySelector('.popup-container');
    const shell = container?.querySelector('.popup-content-container');
    if (!rows.length || !shell) return () => {};

    // The theme's popup is a MULTI-PANEL shell: .popup-content-container holds one
    // .popup-contents per popup on the page, every one display:none, and opening
    // reveals the matching panel. Writing into an existing .popup-contents-output
    // therefore renders into a dormant panel and shows nothing (measured: output
    // 0x0 because its .popup-contents parent computes display:none).
    // So the LP brings its own panel and reuses everything else the shell gives
    // us: the fixed positioning, the backdrop and the theme's own close handling.
    const getPanel = () => {
      let panel = shell.querySelector('[data-families-product-panel]');
      if (!panel) {
        panel = document.createElement('div');
        panel.className = 'popup-contents';
        panel.setAttribute('data-families-product-panel', '');
        // One delegated listener on the panel, not a bind per control per open:
        // the panel outlives every clone, so this survives replaceChildren and
        // never has to be torn down between products.
        panel.addEventListener('click', onPanelClick);
        shell.appendChild(panel);
      }
      return panel;
    };

    const hidePanel = () => {
      const panel = shell.querySelector('[data-families-product-panel]');
      if (!panel) return;
      panel.style.display = 'none';
      // Release the gallery Swipers' observers before the clone goes.
      panel.querySelectorAll('.swiper-initialized').forEach((el) => el.swiper?.destroy(true, true));
      panel.replaceChildren(); // drop the clone so its images can be collected
    };

    // The clone is inert markup: app.js only initialises galleries that exist at
    // DOMContentLoaded, so a popup cloned from a <template> opened with a dead
    // gallery (one image, no thumbnails, no video play, an empty review badge;
    // Tim, 2026-09-03). Swiper is not a global, but every instance app.js built
    // carries its class on `element.swiper.constructor`, so the clone gets the
    // theme's own engine with the theme's own configs, copied verbatim from
    // app.js's product-image-gallery module. Thumbnails exist only when the
    // product has more than one media item, exactly as the snippet renders them.
    const themeSwiper = () => document.querySelector('.product-image-gallery .swiper')?.swiper?.constructor || null;

    // Port of app.js setupVideos: tap the poster to play, tap the video to stop.
    const setupGalleryVideos = (gallery) => {
      gallery.querySelectorAll('.product-template__media--video').forEach((item) => {
        const image = item.querySelector('.media__image');
        const icon = item.querySelector('.media__icon');
        const video = item.querySelector('.media__video');
        if (!image || !icon || !video) return;
        image.addEventListener('click', (event) => {
          event.preventDefault();
          image.classList.add('hidden');
          icon.classList.add('hidden');
          video.classList.remove('hidden');
          video.play();
        });
        video.addEventListener('click', (event) => {
          event.preventDefault();
          video.pause();
          video.classList.add('hidden');
          image.classList.remove('hidden');
          icon.classList.remove('hidden');
        });
      });
    };

    const initialiseGalleries = (contents) => {
      const Swiper = themeSwiper();
      contents.querySelectorAll('.product-image-gallery').forEach((gallery) => {
        // Every slide of an open popup is wanted now; Safari's lazy distance
        // never reaches a slide parked off to the side of the main swiper.
        gallery.querySelectorAll('img[loading="lazy"]').forEach((image) => { image.loading = 'eager'; });
        setupGalleryVideos(gallery);
        if (!Swiper) return;
        const mainEl = gallery.querySelector('.product-template__media-container__main-swiper .swiper');
        const thumbsEl = gallery.querySelector('.product-template__media-container__thumbs-swiper .swiper');
        if (!mainEl || mainEl.swiper) return;
        const thumbs = thumbsEl && !thumbsEl.swiper
          ? new Swiper(thumbsEl, { loop: false, spaceBetween: 12, slidesPerView: 3.8, watchSlidesProgress: true, slideToClickedSlide: true, breakpoints: { 1024: { spaceBetween: 16 } } })
          : null;
        const config = { loop: false, spaceBetween: 12, slidesPerView: 1, breakpoints: { 1024: { spaceBetween: 16 } } };
        if (thumbs) config.thumbs = { swiper: thumbs };
        new Swiper(mainEl, config);
      });
    };

    // The theme refreshes Judge.me 50ms after its own popup trigger; ours never
    // did, so the badge stayed empty. Judge.me is lazy-loaded by the layout: if
    // it is here, refresh; if not, fetch it and it initialises the badge itself.
    const refreshReviewBadge = () => {
      if (window.jdgmCacheServer && typeof window.jdgmCacheServer.reloadAll === 'function') window.jdgmCacheServer.reloadAll();
      else if (typeof window.lpLoadJudgeMe === 'function') window.lpLoadJudgeMe();
    };

    const panelIsOpen = () => shell.querySelector('[data-families-product-panel]')?.style.display === 'block';

    // Mirrors app.js contentLinks lockBackground/unlockBackground exactly. The
    // shell's backdrop was bound at init to the theme's closePopups, which ends
    // in its unlockBackground, and that restores scroll by reading
    // document.body.style.top. Opening with nothing but the overflow class left
    // that inline style empty, so the theme parsed 0 and threw the shopper to the
    // top of the page: measured on the live LP, scrollY 4099 before opening and 0
    // after a backdrop tap. Setting the same inline styles the theme sets means
    // its own unlock restores the right position, and per the theme's comment it
    // is also the only lock iOS Safari honours.
    const lockBackground = () => {
      document.body.classList.add('overflow-y-hidden');
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
    };

    const unlockBackground = () => {
      // Idempotent on purpose: whichever unlock runs second must do nothing.
      // Both unlocks recover the scroll position by parsing body.style.top, so a
      // second run would read the emptied string, parse 0 and throw the shopper
      // to the top of the page.
      if (document.body.style.position !== 'fixed') return;
      document.body.classList.remove('overflow-y-hidden');
      const scrollY = Math.abs(parseInt(document.body.style.top || 0, 10));
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
      });
    };

    // Our unlock always yields to the theme's, because on the backdrop path they
    // both run and only the FIRST one restores the right position. Measured on
    // the live LP: our .popup-bg listener fires before the theme's (the LP script
    // boots on DOMContentLoaded, app.js binds contentLinks later), so unlocking
    // inline gave two scrollTo calls, 4099 then 0, and the shopper landed at the
    // top of the page. The theme's closePopups is fully synchronous, so a
    // macrotask is enough to let it win; our call then hits the guard above and
    // does nothing. On the paths the theme knows nothing about (our close button,
    // Escape) nothing else clears body.style.top, so this still restores 4099,
    // one tick later.
    const deferUnlockBackground = () => {
      window.setTimeout(unlockBackground, 0);
    };

    const close = () => {
      container.classList.remove('active');
      hidePanel();
      deferUnlockBackground();
    };

    // The theme's close buttons live inside its own .popup-contents panels and
    // are bound per element at init (app.js: querySelectorAll('.close-button')
    // .forEach(addEventListener)), so a clone inherits markup but never a
    // handler. Borrowing the markup keeps it pixel-identical to every other
    // popup on the store; the handler is ours. It is position:absolute against
    // .popup-content-container, which is our panel's PARENT rather than the panel
    // itself, so it stays pinned while the panel scrolls under it (measured:
    // panel scrollHeight 1271 vs clientHeight 822, so the panel does scroll).
    const buildCloseButton = () => {
      const themeButton = [...shell.querySelectorAll('.popup-contents .close-button')]
        .find((button) => !button.closest('[data-families-product-panel]'));
      const button = themeButton ? themeButton.cloneNode(true) : document.createElement('button');
      if (!themeButton) {
        // Last resort so the panel can never be unclosable. This LP's shell ships
        // six theme panels to borrow from, but a content-links section rendered
        // with no blocks would ship none.
        button.className = 'close-button z-50 absolute -top-3.5 right-1/2 transform translate-x-1/2 w-11 h-11 flex flex-col justify-center items-center';
        button.textContent = '✕';
      }
      button.setAttribute('type', 'button');
      button.setAttribute('aria-label', 'Close');
      button.setAttribute('data-families-product-panel-close', '');
      return button;
    };

    // Every control below was measured dead on the live page before this handler
    // existed: the close button was absent entirely, and the nutrition table
    // toggle, the four block multipliers and all five accordions did nothing when
    // clicked. The cause is the same for all of them: app.js binds each control
    // once at init, and this panel is cloned out of a <template> long afterwards.
    // Each branch replicates the theme's own handler rather than inventing new
    // behaviour, so the clone behaves like the build-a-bundle popup it came from.
    const onPanelClick = (event) => {
      if (event.target.closest('[data-families-product-panel-close]')) {
        close();
        return;
      }

      // app.js productNutritionInfo: toggle `hidden` on the dropdown and
      // `-rotate-180` on the trigger's chevron. Same classes, so the theme's
      // existing transition on that svg applies unchanged.
      const tableToggle = event.target.closest('.nutrition-table-toggle');
      if (tableToggle) {
        const dropdown = tableToggle.closest('.nutrition-table-container')?.querySelector('.nutrition-table-dropdown');
        if (!dropdown) return;
        const willOpen = dropdown.classList.contains('hidden');
        dropdown.classList.toggle('hidden', !willOpen);
        tableToggle.querySelector('svg')?.classList.toggle('-rotate-180', willOpen);
        return;
      }

      // app.js productNutritionInfo again: the rendered figures are already
      // multiplied by the theme's default, so the per-block value has to come
      // from data-original-value, and the rounding has to match the theme's or
      // the same tap would give different decimals here and on the builder.
      const multiplier = event.target.closest('.product-nutrition-info-content__multiplier-value');
      if (multiplier) {
        if (multiplier.classList.contains('active')) return;
        multiplier.closest('.product-nutrition-info-content__multiplier-container')
          ?.querySelectorAll('.product-nutrition-info-content__multiplier-value')
          .forEach((option) => option.classList.remove('active'));
        multiplier.classList.add('active');

        const factor = parseInt(multiplier.textContent, 10);
        if (!factor) return;
        multiplier.closest('.product-nutrition-info-content')
          ?.querySelectorAll('.product-nutrition-metric')
          .forEach((metric) => {
            const originalValue = metric.dataset.originalValue;
            const valueSpan = metric.querySelector('.product-nutrition-metric-value');
            if (!originalValue || !valueSpan) return;
            valueSpan.textContent = Math.round(originalValue * factor * 100) / 100;
          });
        return;
      }

      // app.js productAccordions. Its judgeme branch is deliberately not copied:
      // it fires only for a `.judgeme-review-accordion` trigger, and
      // snippets/product-accordion.liquid never emits that class, so the branch
      // is unreachable in the theme too.
      const accordionTrigger = event.target.closest('.product-accordion__trigger');
      if (accordionTrigger) {
        accordionTrigger.closest('.product-accordion')?.classList.toggle('active');
      }
    };

    const open = (productId) => {
      // A <template>: its content is inert, so the galleries inside cost nothing
      // until this clone runs. Read .content, not .children.
      const source = document.querySelector(`template[data-families-product-popup="${productId}"]`);
      const contents = source?.content?.firstElementChild?.cloneNode(true);
      if (!contents) return;

      // Strip the add-to-bundle bar. It is the only part of the builder's popup
      // that needs the builder's JS, which is not on this page, so it would sit
      // there as dead controls. Its container's parent IS the fixed bottom bar
      // that holds the quantity selector and every Add to Bundle variant.
      contents.querySelector('.bab-product-popup-contents__atb-container')?.parentElement?.remove();

      showPanel(contents);
    };

    const showPanel = (contents) => {
      const panel = getPanel();
      panel.replaceChildren(buildCloseButton(), contents);
      panel.style.display = 'block';
      container.classList.add('active');
      lockBackground();
      // After display: Swiper measures the slides, and a hidden panel measures 0.
      initialiseGalleries(contents);
      setTimeout(refreshReviewBadge, 50);
    };

    const onClick = (event) => {
      // Never swallow a real control inside the row (quantity steppers when the
      // card is rendered with include_quantity on).
      if (event.target.closest('button, a, input, select')) return;
      const row = event.target.closest('[data-families-product-popup-trigger]');
      if (!row) return;
      open(row.dataset.familiesProductPopupTrigger);
    };

    const onKeydown = (event) => {
      // Escape is ours to add: the theme's shell has no keyboard route out at
      // all. Gated on our own panel being the visible one so this never closes a
      // theme popup, whose behaviour is not ours to change.
      if (event.key === 'Escape') {
        if (panelIsOpen()) close();
        return;
      }
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const row = event.target.closest('[data-families-product-popup-trigger]');
      if (!row || event.target.closest('button, a, input, select')) return;
      event.preventDefault();
      open(row.dataset.familiesProductPopupTrigger);
    };

    // The theme already binds the backdrop, and with lockBackground in place that
    // path now closes us correctly. This is insurance, not duplication: the
    // theme's bind is conditional on the section having rendered at least one
    // popup block (app.js: `if ($popupBg && $popUps.length > 0)`), so a shell
    // with no theme panels would leave the backdrop inert. Both handlers running
    // is harmless because close() is idempotent.
    const backdrop = container.querySelector('.popup-bg');
    const onBackdropClick = () => {
      if (panelIsOpen()) close();
    };
    backdrop?.addEventListener('click', onBackdropClick);

    // Our panel is the only one that is not display:none by default, so it has to
    // be put away again or it would sit under any popup the theme opens next.
    // Two ways out: the theme drops .active on the container when it closes
    // (its own close button and backdrop both do this), and a theme trigger fired
    // while ours is open is about to reveal a different panel. The theme-trigger
    // path deliberately does NOT unlock: the theme's openPopup locks on the very
    // next tick, and unlocking in between would clear body.style.top so that lock
    // would record scroll position 0.
    const onThemeTrigger = (event) => {
      if (event.target.closest('.popup-trigger')) hidePanel();
    };
    // The catch-all for the defect the client reported: whatever drops .active,
    // the body gets its scroll back. Deferred for the same reason close() is: an
    // observer callback is a microtask, so it would otherwise beat the theme's
    // own unlock to body.style.top on any path the theme is also handling.
    const activeObserver = new MutationObserver(() => {
      if (!container.classList.contains('active')) {
        hidePanel();
        deferUnlockBackground();
      }
    });
    activeObserver.observe(container, { attributes: true, attributeFilter: ['class'] });

    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('click', onThemeTrigger, true);

    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeydown);
      document.removeEventListener('click', onThemeTrigger, true);
      backdrop?.removeEventListener('click', onBackdropClick);
      shell.querySelector('[data-families-product-panel]')?.removeEventListener('click', onPanelClick);
      activeObserver.disconnect();
      hidePanel();
      // A section reload while the popup is open must not leave the body locked:
      // the observer is disconnected by this point, so nothing else would.
      unlockBackground();
    };
  };

  const initialiseFamiliesPage = () => {
    if (cleanupCurrentPage) cleanupCurrentPage();

    const hero = document.querySelector(SELECTORS.hero);
    const reasonsRoot = document.querySelector(SELECTORS.reasons);
    const progress = reasonsRoot?.querySelector(SELECTORS.progress);
    const reasons = reasonsRoot ? [...reasonsRoot.querySelectorAll(SELECTORS.reason)] : [];

    if (!hero || !reasonsRoot || !progress || !reasons.length) return;

    if (progress.parentElement !== document.body) document.body.append(progress);

    const label = progress.querySelector(SELECTORS.label);
    const segments = [...progress.querySelectorAll(SELECTORS.segment)];
    const builder = document.querySelector(SELECTORS.builder);
    const cleanupPagination = initialiseBuilderPagination(builder);
    const cleanupGuaranteeQuotes = initialiseGuaranteeQuotes();
    const cleanupDelegatedPopups = initialiseDelegatedPopups();
    const cleanupBundleItemPopups = initialiseBundleItemPopups();
    const mediaQuery = window.matchMedia('(max-width: 1024px)');
    let currentReason = 1;
    let heroIsVisible = true;
    let reasonsInView = false;
    let builderIsVisible = false;
    let scrollTickQueued = false;

    const setActiveReason = (nextIndex) => {
      currentReason = Math.max(1, Math.min(reasons.length, nextIndex));
      if (label) label.textContent = `Reason ${currentReason} of ${reasons.length}`;

      segments.forEach((segment, segmentIndex) => {
        const displayedIndex = segmentIndex + 1;
        segment.classList.toggle('is-active', displayedIndex === currentReason);
        segment.classList.toggle('is-complete', displayedIndex < currentReason);
        if (displayedIndex === currentReason) segment.setAttribute('aria-current', 'step');
        else segment.removeAttribute('aria-current');
      });
    };

    // The active reason is the last one whose top has crossed a fixed trigger
    // line, recomputed from geometry on every scroll frame. A pure function of
    // scroll position updates in both directions with no ratchet, and with
    // reasons around a viewport tall a single line needs no hysteresis: the
    // label can only flicker on sub-pixel jitter exactly at a boundary.
    // The midline, not the scrollspy-conventional near-top (~25%) or the
    // viewport bottom: a reason goes active as it takes over the upper half of
    // the screen, where mobile reading attention sits, so the counter agrees
    // with what is under the eye at the moment it changes; the bottom edge
    // would show "5 of 5" while reason 4 still fills the screen. Measured
    // against visualViewport so the mobile address-bar collapse doesn't drift
    // the line.
    const REASON_TRIGGER_LINE = 0.5;

    const syncActiveReason = () => {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const triggerY = viewportHeight * REASON_TRIGGER_LINE;
      let nextIndex = 1;
      reasons.forEach((reason, index) => {
        if (reason.getBoundingClientRect().top <= triggerY) {
          nextIndex = Number(reason.dataset.reasonIndex) || index + 1;
        }
      });
      setActiveReason(nextIndex);
    };

    const syncProgressVisibility = () => {
      // The bar lives and dies with the reasons section: visible only while some
      // part of it is on screen. The hero gate stops a flash at page top, where
      // the hero and the first reason can share the viewport.
      const shouldShow = reasonsInView && !heroIsVisible;
      progress.classList.toggle('is-visible', shouldShow);
      progress.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    };

    const bundleHasItems = () => {
      const items = window.bundle?.items;
      if (!items) return false;
      return Object.values(items).some((item) => Number(item?.quantity) > 0);
    };

    // The theme's expanded bundle box locks the page (body position:fixed via
    // its lockBackground), and only its own close path unlocks it. So the box
    // must never be hidden while expanded: close it through the theme's own
    // trigger first, and if that somehow fails, leave the box visible rather
    // than strand the shopper on an unscrollable page. (Bug found by Tim:
    // emptying the bundle from inside the expanded box hid it mid-lock.)
    const closeExpandedBuilderBox = (builderBox) => {
      if (!builderBox.classList.contains('active')) return true;
      const trigger = builder.querySelector('.build-a-bundle__info-expand-trigger')
        || builder.querySelector('.build-a-bundle__info-header-trigger');
      trigger?.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      return !builderBox.classList.contains('active');
    };

    const syncBuilderBox = () => {
      if (!builder) return;
      const builderBox = builder.querySelector(SELECTORS.builderBox);
      if (!builderBox) return;

      if (mediaQuery.matches) {
        // Once the bundle holds anything, the sticky box stays visible across
        // the whole page so the shopper never loses their bundle; empty
        // bundles keep the original builder-in-view gating.
        const shouldShow = builderIsVisible || bundleHasItems();
        if (!shouldShow && !closeExpandedBuilderBox(builderBox)) return;
        builderBox.classList.toggle('families-builder-box--in-view', shouldShow);
      } else {
        if (!closeExpandedBuilderBox(builderBox)) return;
        builderBox.classList.remove('families-builder-box--in-view');
      }
    };

    const heroObserver = new IntersectionObserver(([entry]) => {
      heroIsVisible = entry.isIntersecting;
      syncProgressVisibility();
    }, { threshold: 0.05 });

    const reasonsRootObserver = new IntersectionObserver(([entry]) => {
      reasonsInView = entry.isIntersecting;
      if (reasonsInView) syncActiveReason();
      syncProgressVisibility();
    }, { threshold: 0 });

    const builderObserver = builder ? new IntersectionObserver(([entry]) => {
      builderIsVisible = entry.isIntersecting;
      syncBuilderBox();
    }, { threshold: 0.02 }) : null;

    // The theme re-renders the bundle box's contents on every bundle mutation
    // (adds, removes, localStorage preload, the customise-bundle seeding), so
    // its subtree changing is the signal that bundleHasItems() may have
    // flipped. syncBuilderBox only toggles a class on the box element itself,
    // which this observer does not watch, so no feedback loop.
    const builderBoxElement = builder?.querySelector(SELECTORS.builderBox);
    const builderBoxObserver = builderBoxElement ? new MutationObserver(syncBuilderBox) : null;
    builderBoxObserver?.observe(builderBoxElement, { childList: true, subtree: true, characterData: true });

    const onScroll = () => {
      if (!reasonsInView || scrollTickQueued) return;
      scrollTickQueued = true;
      window.requestAnimationFrame(() => {
        scrollTickQueued = false;
        syncActiveReason();
      });
    };

    heroObserver.observe(hero);
    reasonsRootObserver.observe(reasonsRoot);
    if (builderObserver && builder) builderObserver.observe(builder);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    const onViewportChange = () => syncBuilderBox();
    mediaQuery.addEventListener?.('change', onViewportChange);
    syncActiveReason();
    syncProgressVisibility();
    syncBuilderBox();

    cleanupCurrentPage = () => {
      heroObserver.disconnect();
      reasonsRootObserver.disconnect();
      builderObserver?.disconnect();
      builderBoxObserver?.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      mediaQuery.removeEventListener?.('change', onViewportChange);
      cleanupPagination();
      cleanupGuaranteeQuotes();
      cleanupDelegatedPopups();
      cleanupBundleItemPopups();
      if (reasonsRoot.isConnected) reasonsRoot.append(progress);
      else progress.remove();
      cleanupCurrentPage = null;
    };
  };

  // initialiseFamiliesPage returns early unless the reasons scrollspy is on the
  // page (`if (!hero || !reasonsRoot || !progress || !reasons.length) return`),
  // and this LP has no reasons section. Anything that must work regardless of
  // that has to boot on its own, so the dish-row popups are started here rather
  // than from inside it. Its own guard (rows present, popup shell present) is
  // the only condition it needs.
  //
  // "Customise your bundle" is here for the same reason, and it is why that CTA
  // arrived at the builder with nothing in it: the seed was never bound, because
  // the guard above returned first. Each initialiser keeps its own guard, so
  // running them on a page that DOES have a builder stays correct.

  // Lazy videos (2026-09-02 image-loading audit). The theme's stocked.food.custom.js
  // force-plays every <video> on the page at DOMContentLoaded, so with 30 clips on
  // the Families LP a phone pulled 112MB of video in 15 seconds before any tap and
  // image requests to the same CDN host queued behind the streams (the "absolute
  // chaos" accordion photos never arrived in WebKit). Sections now hold sources in
  // data-src with preload="none"; this attaches and plays a clip only when it is
  // near the viewport and pauses it when it leaves. load() after attaching resets
  // paused to true and rejects the theme's pending play() promise, so a clip never
  // starts on its own the moment a source appears. Hidden desktop duplicates never
  // intersect on a phone, so they never load at all.

  // Image warm-up (2026-09-02, Tim on an iPhone: accordion photos arrived late on
  // open and most images landed after they were on screen). Native loading="lazy"
  // is what keeps hidden desktop duplicates and closed popups from loading, but
  // Safari's own lazy distance is short, and no observer margin can reach a slide
  // parked off-screen inside a horizontal carousel: the carousel's own overflow
  // clips it out of every intersection test. So the trigger is the SECTION: once a
  // section is within 1200px of the viewport, every lazy image in it that is
  // actually displayed flips to eager (verified in WebKit: changing loading to
  // eager starts the fetch). display:none duplicates and closed popups have no
  // offsetParent, stay lazy, and load only when shown, so the load-time saving is
  // untouched; everything a reader is about to reach is already fetched, and a
  // collapsed accordion photo is ready before the item opens.
  const initialiseImageWarmup = () => {
    if (!('IntersectionObserver' in window)) return () => {};
    const sections = [...document.querySelectorAll('.shopify-section')].filter((section) => section.querySelector('img[loading="lazy"]'));
    if (!sections.length) return () => {};
    const wake = (section) => {
      section.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        if (image.offsetParent) image.loading = 'eager';
      });
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) wake(entry.target);
      });
    }, { rootMargin: '1200px 0px' });
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  };

  const initialiseLazyVideos = () => {
    const videos = [...document.querySelectorAll('video[data-lp-lazy-video]')];
    if (!videos.length) return () => {};

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Two steps, deliberately. The poster is cheap and is what a reader sees first,
    // so every displayed clip in a section gets it as soon as the section is near.
    // Sources are attached only when the clip itself is in view: with HLS sources
    // WebKit buffers an attached clip whether or not it is playing, and attaching
    // a whole carousel at once made ten seconds on Using your Blocks pull ~100MB
    // (measured 2026-09-02). Poster first, sources on intersection, load() after
    // the sources so the theme's pending play() promise is rejected and nothing
    // starts on its own.
    // The content-links hover clips exist for the desktop mouseenter effect only
    // (the theme's own handler gates on innerWidth > 1023); on a phone they sit at
    // opacity 0 under the main image, so attaching them there is pure waste.
    const isPhoneOnlyHidden = (video) => video.classList.contains('hover-video') && window.innerWidth <= 1023;

    const attachPoster = (video) => {
      if (isPhoneOnlyHidden(video)) return;
      if (video.dataset.lpPoster === 'true') return;
      video.dataset.lpPoster = 'true';
      if (video.dataset.poster) video.setAttribute('poster', video.dataset.poster);
    };

    const attach = (video) => {
      if (isPhoneOnlyHidden(video)) return;
      attachPoster(video);
      if (video.dataset.lpAttached === 'true') return;
      video.dataset.lpAttached = 'true';
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.setAttribute('src', source.dataset.src);
      });
      video.load();
    };

    const play = (video) => {
      if (video.dataset.lpAutoplay === undefined || motionQuery.matches) return;
      video.muted = true;
      const started = video.play();
      started?.catch?.(() => {});
    };

    if (!('IntersectionObserver' in window)) {
      videos.forEach((video) => { attach(video); play(video); });
      return () => {};
    }

    // Reaching one clip attaches sources and poster to every displayed clip in
    // the same section. A carousel's off-screen slides are clipped by its own
    // overflow, so no margin ever reaches them (found on an iPhone, 2026-09-02:
    // the Using your Blocks slides to the right never loaded). Attaching costs
    // nothing but the poster with preload="none"; only play() fetches, and that
    // still happens per clip, when it is actually intersecting.
    const attachGroup = (video) => {
      const section = video.closest('.shopify-section');
      if (!section) return;
      section.querySelectorAll('video[data-lp-lazy-video]').forEach((other) => {
        if (other.offsetParent) attachPoster(other);
      });
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) {
          attach(video);
          attachGroup(video);
          play(video);
        } else if (!video.paused) {
          video.pause();
        }
      });
    }, { rootMargin: '800px 0px' });

    videos.forEach((video) => observer.observe(video));

    // Section-level safety net (2026-09-02, Tim on an iPhone: Using your Blocks and
    // the footer clip showed nothing on the Skeptics LP while Playwright's WebKit
    // showed them fine). Anything that creates <video> elements after boot, a
    // carousel cloning its slides on a real touch device for example, leaves
    // clips this observer has never seen. So each section that holds lazy clips
    // is watched too: when it comes within 800px, every lazy clip inside it,
    // clones included, is attached, and any clip not yet observed is handed to the
    // per-clip observer above so playback still follows the viewport.
    const observed = new WeakSet(videos);
    const sections = [...new Set(videos.map((video) => video.closest('.shopify-section')).filter(Boolean))];
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.querySelectorAll('video[data-lp-lazy-video]').forEach((video) => {
          if (video.offsetParent) attachPoster(video);
          if (!observed.has(video)) {
            observed.add(video);
            observer.observe(video);
          }
        });
      });
    }, { rootMargin: '800px 0px' });
    sections.forEach((section) => sectionObserver.observe(section));

    return () => {
      observer.disconnect();
      sectionObserver.disconnect();
    };
  };


  // Opt-in on-device diagnostics (2026-09-02): open the page with ?lpdebug=1 and
  // every uncaught error and rejected promise is printed in a fixed overlay, so a
  // phone-only failure can be read without a cable. Inert without the parameter.
  const lpDebug = /[?&]lpdebug=1/.test(window.location.search);
  const lpReport = (label, error) => {
    if (!lpDebug) return;
    let box = document.getElementById('lp-debug');
    if (!box) {
      box = document.createElement('pre');
      box.id = 'lp-debug';
      box.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:2147483647;max-height:40vh;overflow:auto;margin:0;padding:8px;background:#111;color:#0f0;font:11px/1.4 monospace;white-space:pre-wrap;';
      document.body.appendChild(box);
    }
    const message = error && (error.stack || error.message) ? (error.stack || error.message) : String(error);
    box.textContent += '[' + label + '] ' + message + '\n';
  };
  if (lpDebug) {
    window.addEventListener('error', (event) => lpReport('error', event.error || event.message));
    window.addEventListener('unhandledrejection', (event) => lpReport('rejection', event.reason));
    document.addEventListener('DOMContentLoaded', () => lpReport('boot', 'script ran, UA ' + navigator.userAgent.slice(0, 80)));
  }

  // One initialiser failing must not take the others with it: run each in its own
  // try/catch and return a no-op cleanup for the one that failed.
  const guarded = (label, initialise) => {
    try {
      return initialise() || (() => {});
    } catch (error) {
      lpReport(label, error);
      return () => {};
    }
  };

  let cleanupStandalone = null;
  const initialiseStandalone = () => {
    cleanupStandalone?.();
    const cleanups = [
      guarded('lazyVideos', initialiseLazyVideos),
      guarded('imageWarmup', initialiseImageWarmup),
      guarded('dishRowPopups', initialiseDishRowPopups),
      guarded('infoPopups', initialiseInfoPopups),
      guarded('customiseBundle', initialiseCustomiseBundle),
      guarded('bundleEquations', initialiseBundleEquations),
    ];
    cleanupStandalone = () => cleanups.forEach((cleanup) => cleanup?.());
  };

  const boot = () => {
    initialiseStandalone();
    initialiseFamiliesPage();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', () => {
    cleanupCurrentPage?.();
    cleanupStandalone?.();
    cleanupStandalone = null;
  });
})();
